import pb from '../pocketbaseClient';
import jobDocumentService from './JobDocumentService';
import assessmentService from './AssessmentService';
import { cmsProxyUrl } from './JobListingsService';
import { describeJobFiles, packageBaseName, zipPackages } from '../utils/jobPackage';

/**
 * Hands a job's export back: gathers what describeJobFiles lists from the
 * three places it lives (PocketBase records, the CMS file store, the
 * PocketBase file store) and packs it.
 */

/** How many jobs fetch their HTML files at once. Enough to be quick, few enough not to flood the proxy. */
const PARALLEL_JOBS = 4;

async function fetchBytes(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return new Uint8Array(await response.arrayBuffer());
}

/**
 * The content of one described file. Text files carry it already; the two HTML
 * files are fetched, byte for byte as they were uploaded.
 */
export async function loadFileData(file, { job, assessment }, fileToken) {
  if (!file.remote) return file.text;
  if (file.remote === 'campaign') return fetchBytes(cmsProxyUrl(job.campaignUrl));
  const token = fileToken ?? (await pb.files.getToken());
  return fetchBytes(pb.files.getURL(assessment, assessment.filename, { token }));
}

/** The PocketBase records of one job. A failure only costs the files they hold. */
export async function loadJobSources(job) {
  const [documents, assessment] = await Promise.all([
    jobDocumentService.getByJobId(job.id),
    assessmentService.getByJobId(job.id),
  ]);
  return {
    documents: documents.data || null,
    assessment: assessment.data || null,
    error: documents.success ? assessment.error : documents.error,
  };
}

/** Every available file of one job, loaded. Files that fail are reported, not fatal. */
export async function collectJobPackage(job, sources, fileToken) {
  const described = describeJobFiles({ job, ...sources });
  const files = [];
  const failed = [];

  await Promise.all(
    described
      .filter((file) => file.available)
      .map(async (file) => {
        try {
          files.push({ filename: file.filename, data: await loadFileData(file, { job, ...sources }, fileToken) });
        } catch (error) {
          failed.push({ filename: file.filename, error: error.message });
        }
      })
  );

  return {
    baseName: packageBaseName({ job, ...sources }),
    files,
    failed,
    // No stored base name means the job was imported before the originals were
    // kept, so its JSON and storyboard are missing for that reason alone.
    withoutOriginals: !sources.documents?.baseName,
  };
}

/** Run `fn` over `items`, at most `limit` at a time, in order of completion. */
async function forEachLimited(items, limit, fn) {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      await fn(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

/**
 * One ZIP with the export of every given job.
 *
 * The records come in two list calls rather than two per job; only the HTML
 * files are fetched per job.
 */
export async function buildAllJobsZip(jobs, onProgress) {
  const [documents, assessments] = await Promise.all([jobDocumentService.getAll(), assessmentService.getAll()]);
  if (!documents.success && !assessments.success) {
    return { success: false, error: documents.error };
  }

  const documentsByJob = new Map(documents.data.map((d) => [String(d.cmsJobId), d]));
  const assessmentsByJob = new Map(assessments.data.map((a) => [String(a.cmsJobId), a]));
  const fileToken = await pb.files.getToken().catch(() => undefined);

  const packages = new Array(jobs.length);
  let done = 0;
  onProgress?.({ done, total: jobs.length });

  await forEachLimited(jobs, PARALLEL_JOBS, async (job, index) => {
    packages[index] = await collectJobPackage(
      job,
      {
        documents: documentsByJob.get(String(job.id)) || null,
        assessment: assessmentsByJob.get(String(job.id)) || null,
      },
      fileToken
    );
    done += 1;
    onProgress?.({ done, total: jobs.length });
  });

  return {
    success: true,
    data: {
      bytes: zipPackages(packages),
      fileCount: packages.reduce((sum, p) => sum + p.files.length, 0),
      failed: packages.flatMap((p) => p.failed),
      withoutOriginals: packages.filter((p) => p.withoutOriginals).length,
      // Kept apart: a missing collection is worth saying, and the ZIP is still
      // useful without it.
      warning: documents.success ? assessments.error : documents.error,
    },
  };
}
